import { captureException } from './errorLogger'

/**
 * Structured frontend logger.
 * Runtime code should use this module instead of direct console calls.
 */

const LEVELS = new Set(['debug', 'info', 'warn', 'error'])

function safeJson(value) {
    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

export function buildLogEvent(level, event, meta = {}) {
    const normalizedLevel = LEVELS.has(level) ? level : 'info'
    const metadata = meta && typeof meta === 'object' ? meta : { value: meta }
    return {
        timestamp: new Date().toISOString(),
        level: normalizedLevel,
        event: String(event || 'app.event'),
        message: String(metadata.message || event || 'Application event'),
        reqId: metadata.reqId || metadata.requestId || null,
        userId: metadata.userId || null,
        companyId: metadata.companyId || null,
        route: metadata.route || (typeof window !== 'undefined' ? window.location.pathname : null),
        method: metadata.method || null,
        durationMs: metadata.durationMs ?? null,
        status: metadata.status ?? null,
        metadata,
    }
}

export function normalizeLogError(args) {
    const existingError = args.find((arg) => arg instanceof Error)
    if (existingError) return existingError

    const message = args.map((arg) => {
        if (typeof arg === 'string') return arg
        return safeJson(arg)
    }).join(' ')

    return new Error(message || 'Unknown application error')
}

function devConsole(level, event) {
    if (import.meta.env.DEV) {
        const browserConsole = globalThis.console
        browserConsole[level](event)
    }
}

export function log(level, event, meta = {}) {
    const structured = buildLogEvent(level, event, meta)
    if (structured.level === 'error') {
        const error = meta instanceof Error
            ? meta
            : meta?.error instanceof Error
                ? meta.error
                : normalizeLogError([structured.event, structured.metadata])
        if (import.meta.env.DEV) devConsole('error', structured)
        else captureException(error, structured)
        return structured
    }
    if (import.meta.env.DEV) {
        const consoleLevel = structured.level === 'debug' ? 'info' : structured.level
        devConsole(consoleLevel, structured)
    }
    return structured
}

export function logError(event, error, meta = {}) {
    return log('error', event, { ...meta, error, message: error?.message || meta.message })
}

export function logWarn(event, meta = {}) {
    return log('warn', event, meta)
}

export function logInfo(event, meta = {}) {
    return log('info', event, meta)
}

export function logDebug(event, meta = {}) {
    return log('debug', event, meta)
}

export function logDevError(...args) {
    return log('error', String(args[0] || 'app.error'), { args })
}

export function logDevWarn(...args) {
    return log('warn', String(args[0] || 'app.warn'), { args })
}

export function logDevInfo(...args) {
    return log('info', String(args[0] || 'app.info'), { args })
}
